import React, { useMemo } from 'react';
import { DataTable } from '@components/organisms/Documents/components/DataTable';
import { BudgetLineItem, DelegatedTaskCost } from '@models/lineItem.model';
import { useTranslation } from '@hooks/useTranslation';

type InventoryItem = {
  totalCost?: number | null;
  name?: string;
  equipmentId?: string;
};

interface AllItemsTabProps {
  inventory: InventoryItem[];
  delegatedTaskCosts: DelegatedTaskCost[];
  lineItems: BudgetLineItem[];
}

type UnifiedRow = {
  id: string;
  category: 'Material' | 'Equipment' | 'Delegated' | 'Line Item';
  name: string;
  totalCost: number;
};

const formatCurrency = (value: number) => {
  return `$ ${value.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
};

const categoryColors: Record<string, string> = {
  Material: 'text-blue-700 bg-blue-100',
  Equipment: 'text-gray-700 bg-gray-100',
  Delegated: 'text-orange-700 bg-orange-100',
  'Line Item': 'text-purple-700 bg-purple-100',
};

export const AllItemsTab: React.FC<AllItemsTabProps> = ({
  inventory,
  delegatedTaskCosts,
  lineItems,
}) => {
  const tProjectComponents = useTranslation('projectComponents');
  const t = tProjectComponents.allItemsTab;
  const unifiedData = useMemo(() => {
    const rows: UnifiedRow[] = [];

    inventory.forEach((item, i) => {
      const cost = item.totalCost || 0;
      if (cost === 0 && !item.totalCost) return;
      rows.push({
        id: `inv-${i}`,
        category: item.equipmentId ? 'Equipment' : 'Material',
        name: item.name || '-',
        totalCost: cost,
      });
    });

    delegatedTaskCosts.forEach((d) => {
      rows.push({
        id: `del-${d.taskId}`,
        category: 'Delegated',
        name: `${d.taskName} (${d.organizationName})`,
        totalCost: d.budget || 0,
      });
    });

    lineItems.forEach((e) => {
      rows.push({
        id: `exp-${e.lineItemId}`,
        category: 'Line Item',
        name: e.description,
        totalCost: e.cost || 0,
      });
    });

    return rows;
  }, [inventory, delegatedTaskCosts, lineItems]);

  const columns = useMemo(() => {
    const categoryLabels: Record<string, string> = {
      Material: t.categoryMaterial,
      Equipment: t.categoryEquipment,
      Delegated: t.categoryDelegated,
      'Line Item': t.categoryLineItem,
    };
    return [
      {
        key: 'category',
        label: t.colCategory,
        className: 'col-span-1',
        render: (v: string) => (
          <span
            className={`text-xs font-medium px-2 py-0.5 rounded-full ${
              categoryColors[v] || 'text-gray-700 bg-gray-100'
            }`}>
            {categoryLabels[v] || v}
          </span>
        ),
      },
      {
        key: 'name',
        label: t.colNameDescription,
        className: 'col-span-2',
        render: (v: string) => v || '-',
      },
      {
        key: 'totalCost',
        label: t.colTotalCost,
        className: 'col-span-1',
        render: (v: number) => formatCurrency(v || 0),
      },
    ];
  }, [t]);

  const grandTotal = unifiedData.reduce((sum, r) => sum + r.totalCost, 0);

  return (
    <div>
      <DataTable
        height={500}
        loading={false}
        columns={columns}
        data={unifiedData}
        compact
        emptyMessage={t.emptyMessage}
      />

      <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-md mt-2">
        <div className="flex flex-col gap-0.5">
          <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
            {t.grandTotal}
          </p>
          <p className="text-sm font-bold text-gray-900">
            {formatCurrency(grandTotal)}
          </p>
        </div>
      </div>
    </div>
  );
};
